import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import {
  api,
  endpoints,
  startPeriodicRefresh,
  stopPeriodicRefresh,
} from '../../services/api';
import { translateAxiosError } from '../../utils/errorTranslator';

// Async thunks
export const loginUser = createAsyncThunk(
  'auth/loginUser',
  async ({ username, password }, { rejectWithValue }) => {
    try {
      const response = await api.post(endpoints.login, {
        username,
        password,
      });
      return response.data;
    } catch (error) {
      console.error('Login error:', error);
      console.error('Error response:', error.response);
      console.error('Error response data:', error.response?.data);

      const errorMessage = translateAxiosError(error);
      return rejectWithValue(errorMessage);
    }
  }
);

export const signupUser = createAsyncThunk(
  'auth/signupUser',
  async (signupData, { rejectWithValue }) => {
    try {
      const response = await api.post(endpoints.signup, signupData);
      return response.data;
    } catch (error) {
      const errorMessage = translateAxiosError(error);
      return rejectWithValue(errorMessage);
    }
  }
);

export const refreshToken = createAsyncThunk(
  'auth/refreshToken',
  async (_, { rejectWithValue }) => {
    try {
      const refresh_token = localStorage.getItem('refresh_token');
      if (!refresh_token) {
        throw new Error('No refresh token');
      }

      const response = await api.post(endpoints.refresh, {
        refresh_token,
      });
      return response.data;
    } catch (error) {
      const errorMessage = translateAxiosError(error);
      return rejectWithValue(errorMessage);
    }
  }
);

export const getUserInfo = createAsyncThunk(
  'auth/getUserInfo',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get(endpoints.userMe);
      return response.data;
    } catch (error) {
      const errorMessage = translateAxiosError(error);
      return rejectWithValue(errorMessage);
    }
  }
);

// Helper functions to load from localStorage
const loadUserFromStorage = () => {
  try {
    const userStr = localStorage.getItem('user');
    return userStr ? JSON.parse(userStr) : null;
  } catch {
    return null;
  }
};

const initialState = {
  user: loadUserFromStorage(),
  token: localStorage.getItem('access_token'),
  refreshToken: localStorage.getItem('refresh_token'),
  // Only authenticated if we have both token and user data
  isAuthenticated:
    !!localStorage.getItem('access_token') &&
    !!localStorage.getItem('refresh_token') &&
    !!loadUserFromStorage(),
  loading: false,
  error: null,
};

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    logout: state => {
      state.user = null;
      state.token = null;
      state.refreshToken = null;
      state.isAuthenticated = false;
      state.error = null;
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
      localStorage.removeItem('user');
      localStorage.removeItem('isAuthenticated');

      // Stop periodic token refresh
      stopPeriodicRefresh();
    },
    clearError: state => {
      state.error = null;
    },
    setToken: (state, action) => {
      state.token = action.payload;
      // Only set isAuthenticated to true if we have both token and user data
      // This prevents the mismatch that could cause infinite loops
      state.isAuthenticated = !!(action.payload && state.user);
      localStorage.setItem('isAuthenticated', state.isAuthenticated.toString());
    },
    updateUser: (state, action) => {
      state.user = action.payload;
      // Update isAuthenticated based on user and token
      state.isAuthenticated = !!(state.token && action.payload);
      localStorage.setItem('user', JSON.stringify(action.payload));
      localStorage.setItem('isAuthenticated', state.isAuthenticated.toString());
    },
  },
  extraReducers: builder => {
    builder
      // Login
      .addCase(loginUser.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(loginUser.fulfilled, (state, action) => {
        state.loading = false;
        state.token = action.payload.access_token;
        state.refreshToken = action.payload.refresh_token;
        // Don't set isAuthenticated to true yet - wait for getUserInfo to complete
        // This prevents the mismatch that causes infinite loops
        state.isAuthenticated = false;
        localStorage.setItem('access_token', action.payload.access_token);
        localStorage.setItem('refresh_token', action.payload.refresh_token);
        localStorage.setItem('isAuthenticated', 'false');

        // Start periodic token refresh
        startPeriodicRefresh();
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Signup
      .addCase(signupUser.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(signupUser.fulfilled, state => {
        state.loading = false;
        state.error = null;
        // Don't set authentication state - user needs admin approval
      })
      .addCase(signupUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Refresh token
      .addCase(refreshToken.pending, state => {
        state.loading = true;
      })
      .addCase(refreshToken.fulfilled, (state, action) => {
        state.loading = false;
        state.token = action.payload.access_token;
        // Keep existing refresh token, only update access token
        state.isAuthenticated = !!(
          action.payload.access_token &&
          state.user &&
          state.refreshToken
        );
        localStorage.setItem('access_token', action.payload.access_token);
        localStorage.setItem(
          'isAuthenticated',
          state.isAuthenticated.toString()
        );
      })
      .addCase(refreshToken.rejected, state => {
        state.loading = false;
        state.token = null;
        state.refreshToken = null;
        state.user = null;
        state.isAuthenticated = false;
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        localStorage.removeItem('user');
        localStorage.removeItem('isAuthenticated');

        // Stop periodic token refresh on failure
        stopPeriodicRefresh();
      })
      // Get user info
      .addCase(getUserInfo.pending, state => {
        state.loading = true;
      })
      .addCase(getUserInfo.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload;
        state.isAuthenticated = true;
        localStorage.setItem('user', JSON.stringify(action.payload));
        localStorage.setItem('isAuthenticated', 'true');
      })
      .addCase(getUserInfo.rejected, (state, action) => {
        state.loading = false;
        // Only clear on authentication errors when no refresh token is available
        const error = action.payload || action.error;
        const isAuthError =
          error?.includes('401') ||
          error?.includes('unauthorized') ||
          error?.includes('token') ||
          error?.includes('authentication');

        if (isAuthError && !localStorage.getItem('refresh_token')) {
          // Authentication error and no refresh token - clear everything
          state.user = null;
          state.isAuthenticated = false;
          localStorage.removeItem('user');
          localStorage.setItem('isAuthenticated', 'false');
        }
        // If refresh token exists, API interceptor will handle refresh
      });
  },
});

export const { logout, clearError, setToken, updateUser } = authSlice.actions;
export default authSlice.reducer;
